import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format, addDays, startOfToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { useAuthStore } from '../store/useStore';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import CartaExpressWidget from '../components/widgets/CartaExpressWidget';
import WidgetIntegrationPanel from '../components/widgets/WidgetIntegrationPanel';
import {
  Code2, Copy, Palette, Eye, Share2, MessageSquare, Facebook, Instagram, Twitter,
  Link2, BookOpen, HelpCircle, UtensilsCrossed, ShoppingCart, Plus,
  X, Star, ChefHat, Wand2, Loader2, Settings, Sparkles, Minimize2, Zap, Upload, Image,
  ChevronLeft, ChevronRight, Users, Calendar, Clock, User, Gift, Search,
  Heart, Cake, GraduationCap, Briefcase, Gem, Home, Ban, Users2,
  AlertTriangle, Accessibility, Baby, Check, Send, Globe, CreditCard, Save
} from 'lucide-react';

/* ── Constants ──────────────────────────────────────────────── */
const THEME_PRESETS = {
  elegant:    { name:'Elegante',     description:'Sofisticado y profesional', Icon:Sparkles,  primaryColor:'#1a1a1a', borderRadius:'16px', theme:'dark'  },
  minimal:    { name:'Minimalista',  description:'Limpio y moderno',          Icon:Minimize2, primaryColor:'#F97316', borderRadius:'12px', theme:'light' },
  innovative: { name:'Innovador',    description:'Dinámico y colorido',       Icon:Zap,       primaryColor:'#8B5CF6', borderRadius:'24px', theme:'dark'  },
};

const OCCASIONS = [
  { id:'anniversary', label:'Aniversario',           Icon:Heart },
  { id:'romantic',    label:'Cita romántica',         Icon:Heart },
  { id:'birthday',    label:'Cumpleaños',             Icon:Cake },
  { id:'friends',     label:'Entre amigos',           Icon:Users2 },
  { id:'graduation',  label:'Graduación',             Icon:GraduationCap },
  { id:'business',    label:'Negocios',               Icon:Briefcase },
  { id:'proposal',    label:'Propuesta',              Icon:Gem },
  { id:'family',      label:'Reunión familiar',       Icon:Home },
  { id:'none',        label:'No la hay',              Icon:Ban },
];
const SPECIAL_REQS = [
  { id:'accessibility', label:'Apoyo especial', Icon:Accessibility },
  { id:'baby_seat',     label:'Silla de bebé',  Icon:Baby },
];
const QUICK_TIMES = ['12:00','12:30','13:00','13:30','14:00','14:30','15:00','19:00','19:30','20:00','20:30','21:00','21:30','22:00','22:30'];
const PRODUCTION_URL = 'https://okreserva.okgo.mx';

const LANGUAGES = [
  { code:'es', label:'🇲🇽 Español', name:'Español' },
  { code:'en', label:'🇺🇸 English', name:'English' },
  { code:'fr', label:'🇫🇷 Français', name:'Français' },
  { code:'it', label:'🇮🇹 Italiano', name:'Italiano' },
  { code:'pt', label:'🇧🇷 Português', name:'Português' },
  { code:'de', label:'🇩🇪 Deutsch', name:'Deutsch' },
  { code:'zh', label:'🇨🇳 中文', name:'中文' },
  { code:'ko', label:'🇰🇷 한국어', name:'한국어' },
  { code:'ja', label:'🇯🇵 日本語', name:'日本語' },
  { code:'ar', label:'🇸🇦 العربية', name:'العربية' },
];

const WIDGET_TRANSLATIONS = {
  es:{ title:'Reservación', step1:'Tu información', step2:'Fecha & hora', step3:'Elige tu menú', step4:'Confirma tu reservación', name:'Nombre', email:'Correo', whatsapp:'WhatsApp', guests:'Comensales', date:'Fecha', time:'Hora', occasion:'Ocasión', allergies:'Alergias', requests:'Petición especial', next:'Siguiente', back:'Atrás', confirm:'Confirmar Reservación', pay:'Confirmar y pagar', payAtRest:'Pagar en el restaurante', payOnline:'Pagar en línea', payWallet:'OkWallet — Pagar con cartera', paymentMethod:'Método de pago', addToOrder:'Agregar al pedido', yourOrder:'Tu pedido', estimatedTotal:'Total estimado', powered:'Propulsado por', selectLang:'Idioma' },
  en:{ title:'Reservation', step1:'Your info', step2:'Date & time', step3:'Choose your menu', step4:'Confirm your reservation', name:'Name', email:'Email', whatsapp:'WhatsApp', guests:'Guests', date:'Date', time:'Time', occasion:'Occasion', allergies:'Allergies', requests:'Special request', next:'Next', back:'Back', confirm:'Confirm Reservation', pay:'Confirm & pay', payAtRest:'Pay at restaurant', payOnline:'Pay online', payWallet:'OkWallet — Pay from wallet', paymentMethod:'Payment method', addToOrder:'Add to order', yourOrder:'Your order', estimatedTotal:'Estimated total', powered:'Powered by', selectLang:'Language' },
  fr:{ title:'Réservation', step1:'Vos infos', step2:'Date & heure', step3:'Choisir le menu', step4:'Confirmer', name:'Nom', email:'Email', whatsapp:'WhatsApp', guests:'Personnes', date:'Date', time:'Heure', occasion:'Occasion', allergies:'Allergies', requests:'Demande spéciale', next:'Suivant', back:'Retour', confirm:'Confirmer', pay:'Confirmer et payer', payAtRest:'Payer au restaurant', payOnline:'Payer en ligne', payWallet:'OkWallet — Payer par portefeuille', paymentMethod:'Mode de paiement', addToOrder:'Ajouter', yourOrder:'Votre commande', estimatedTotal:'Total estimé', powered:'Propulsé par', selectLang:'Langue' },
  it:{ title:'Prenotazione', step1:'I tuoi dati', step2:'Data & ora', step3:'Scegli il menu', step4:'Conferma', name:'Nome', email:'Email', whatsapp:'WhatsApp', guests:'Ospiti', date:'Data', time:'Ora', occasion:'Occasione', allergies:'Allergie', requests:'Richiesta speciale', next:'Avanti', back:'Indietro', confirm:'Conferma prenotazione', pay:'Conferma e paga', payAtRest:'Paga al ristorante', payOnline:'Paga online', payWallet:'OkWallet — Paga con portafoglio', paymentMethod:'Metodo di pagamento', addToOrder:'Aggiungi', yourOrder:'Il tuo ordine', estimatedTotal:'Totale stimato', powered:'Powered by', selectLang:'Lingua' },
  pt:{ title:'Reserva', step1:'Seus dados', step2:'Data & hora', step3:'Escolha o cardápio', step4:'Confirmar reserva', name:'Nome', email:'Email', whatsapp:'WhatsApp', guests:'Pessoas', date:'Data', time:'Hora', occasion:'Ocasião', allergies:'Alergias', requests:'Pedido especial', next:'Próximo', back:'Voltar', confirm:'Confirmar reserva', pay:'Confirmar e pagar', payAtRest:'Pagar no restaurante', payOnline:'Pagar online', payWallet:'OkWallet — Pagar com carteira', paymentMethod:'Forma de pagamento', addToOrder:'Adicionar', yourOrder:'Seu pedido', estimatedTotal:'Total estimado', powered:'Powered by', selectLang:'Idioma' },
  de:{ title:'Reservierung', step1:'Ihre Daten', step2:'Datum & Uhrzeit', step3:'Menü wählen', step4:'Bestätigen', name:'Name', email:'E-Mail', whatsapp:'WhatsApp', guests:'Personen', date:'Datum', time:'Uhrzeit', occasion:'Anlass', allergies:'Allergien', requests:'Sonderwunsch', next:'Weiter', back:'Zurück', confirm:'Reservierung bestätigen', pay:'Bestätigen & zahlen', payAtRest:'Im Restaurant zahlen', payOnline:'Online zahlen', payWallet:'OkWallet — Aus Geldbörse zahlen', paymentMethod:'Zahlungsmethode', addToOrder:'Hinzufügen', yourOrder:'Ihre Bestellung', estimatedTotal:'Geschätzter Gesamtbetrag', powered:'Powered by', selectLang:'Sprache' },
  zh:{ title:'预约', step1:'您的信息', step2:'日期和时间', step3:'选择菜单', step4:'确认预约', name:'姓名', email:'邮箱', whatsapp:'WhatsApp', guests:'人数', date:'日期', time:'时间', occasion:'场合', allergies:'过敏原', requests:'特殊要求', next:'下一步', back:'返回', confirm:'确认预约', pay:'确认并支付', payAtRest:'到餐厅付款', payOnline:'在线支付', payWallet:'OkWallet — 钱包支付', paymentMethod:'支付方式', addToOrder:'加入订单', yourOrder:'您的订单', estimatedTotal:'预计总计', powered:'技术支持', selectLang:'语言' },
  ko:{ title:'예약', step1:'정보 입력', step2:'날짜 & 시간', step3:'메뉴 선택', step4:'예약 확인', name:'이름', email:'이메일', whatsapp:'WhatsApp', guests:'인원', date:'날짜', time:'시간', occasion:'경우', allergies:'알레르기', requests:'특별 요청', next:'다음', back:'이전', confirm:'예약 확인', pay:'확인 및 결제', payAtRest:'레스토랑에서 결제', payOnline:'온라인 결제', payWallet:'OkWallet — 지갑으로 결제', paymentMethod:'결제 방법', addToOrder:'주문에 추가', yourOrder:'내 주문', estimatedTotal:'예상 합계', powered:'Powered by', selectLang:'언어' },
  ja:{ title:'予約', step1:'お客様情報', step2:'日時', step3:'メニューを選択', step4:'予約を確認', name:'名前', email:'メール', whatsapp:'WhatsApp', guests:'人数', date:'日付', time:'時間', occasion:'機会', allergies:'アレルギー', requests:'特別リクエスト', next:'次へ', back:'戻る', confirm:'予約を確定', pay:'確定して支払う', payAtRest:'レストランで支払う', payOnline:'オンラインで支払う', payWallet:'OkWallet — 財布で支払う', paymentMethod:'お支払い方法', addToOrder:'注文に追加', yourOrder:'ご注文', estimatedTotal:'合計金額（目安）', powered:'提供元', selectLang:'言語' },
  ar:{ title:'حجز', step1:'معلوماتك', step2:'التاريخ والوقت', step3:'اختر القائمة', step4:'تأكيد الحجز', name:'الاسم', email:'البريد الإلكتروني', whatsapp:'واتساب', guests:'الضيوف', date:'التاريخ', time:'الوقت', occasion:'المناسبة', allergies:'الحساسية', requests:'طلب خاص', next:'التالي', back:'رجوع', confirm:'تأكيد الحجز', pay:'تأكيد والدفع', payAtRest:'الدفع في المطعم', payOnline:'الدفع عبر الإنترنت', payWallet:'OkWallet — الدفع بالمحفظة', paymentMethod:'طريقة الدفع', addToOrder:'أضف للطلب', yourOrder:'طلبك', estimatedTotal:'الإجمالي المتوقع', powered:'مدعوم من', selectLang:'اللغة' },
};

const TODAY = startOfToday();

/* ── Main Page ───────────────────────────────────────────────── */
export default function WidgetCartaExpress() {
  const { user } = useAuthStore();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('design');
  const [selectedPreset, setSelectedPreset] = useState('minimal');
  const [restaurantLogo, setRestaurantLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [menus, setMenus] = useState([]);

  const [config, setConfig] = useState({
    theme: 'dark',
    primaryColor: '#F97316',
    restaurantName: 'Mi Restaurante',
    restaurantLogo: null,
    borderRadius: '12px',
    showPrice: true,
    showAllergens: true,
    showCategories: true,
    showFeaturedFirst: true,
    language: 'es',
    paymentMethods: ['restaurant'],
  });

  const restaurantId = user?.restaurant_id || 'YOUR_RESTAURANT_ID';
  const [saving, setSaving] = useState(false);

  // Load config from localStorage on mount
  useEffect(() => {
    if (!restaurantId || restaurantId === 'YOUR_RESTAURANT_ID') return;
    try {
      const saved = localStorage.getItem(`widget_carta_config_${restaurantId}`);
      if (saved) setConfig(JSON.parse(saved));
    } catch {}
  }, [restaurantId]);

  const saveConfig = () => {
    setSaving(true);
    try {
      localStorage.setItem(`widget_carta_config_${restaurantId}`, JSON.stringify(config));
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setTimeout(() => setSaving(false), 800); }
  };

  const loadMenus = useCallback(async () => {
    setLoadingMenus(true);
    try {
      const res = await api.get('/menus');
      const active = (res.data || []).filter(m => m.is_active !== false);
      const withDishes = await Promise.all(active.map(async m => {
        try { const dr = await api.get(`/menus/${m.id}/dishes`); return { ...m, dishes: dr.data || [] }; }
        catch { return { ...m, dishes: [] }; }
      }));
      setMenus(withDishes);
    } catch { toast.error('Error al cargar menús'); }
    finally { setLoadingMenus(false); }
  }, []);

  useEffect(() => { loadMenus(); }, [loadMenus]);

  const applyPreset = (key) => {
    const p = THEME_PRESETS[key];
    setSelectedPreset(key);
    setConfig(prev => ({ ...prev, primaryColor: p.primaryColor, theme: p.theme, borderRadius: p.borderRadius }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('La imagen no debe superar 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setRestaurantLogo(ev.target.result); setConfig(p => ({ ...p, restaurantLogo: ev.target.result })); };
    reader.readAsDataURL(file);
    toast.success('Logo cargado');
  };

  const generateEmbedCode = () => `<!-- OkReserva Carta Express Widget -->
<iframe
  src="${PRODUCTION_URL}/widget/carta/${restaurantId}?color=${config.primaryColor.replace('#','')}&theme=${config.theme}&radius=${config.borderRadius.replace('px','')}&lang=${config.language}"
  width="100%"
  height="700"
  frameborder="0"
  style="border-radius: ${config.borderRadius}; max-width: 960px; display: block; margin: 0 auto; border: none;"
  loading="lazy"
  title="Carta Express - ${config.restaurantName}"
></iframe>`;

  const totalDishes = menus.reduce((s, m) => s + (m.dishes?.length || 0), 0);

  return (
    <div className="min-h-screen" data-testid="widget-carta-page">
      <TopBar title="Widget Carta Express" helpSection="widget-carta-express" actions={
        <Button onClick={saveConfig} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
        </Button>
      } />

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="design" className="flex items-center gap-2"><Palette className="h-4 w-4" /> Diseño</TabsTrigger>
            <TabsTrigger value="code"   className="flex items-center gap-2"><Code2 className="h-4 w-4" /> Integración</TabsTrigger>
            <TabsTrigger value="share"  className="flex items-center gap-2"><Share2 className="h-4 w-4" /> Compartir</TabsTrigger>
            <TabsTrigger value="carta"  className="flex items-center gap-2"><UtensilsCrossed className="h-4 w-4" /> Carta</TabsTrigger>
          </TabsList>

          {/* ── DESIGN TAB ── */}
          <TabsContent value="design">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left config */}
              <div className="space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><Wand2 className="h-5 w-5 text-primary" /> Temas Predefinidos</CardTitle>
                    <CardDescription>Selecciona un estilo base</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(THEME_PRESETS).map(([key, preset]) => (
                        <button key={key} onClick={() => applyPreset(key)}
                          className={cn('p-4 rounded-xl border-2 text-center transition-all', selectedPreset===key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted')}>
                          <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: preset.primaryColor }}>
                            <preset.Icon className="h-5 w-5 text-white" />
                          </div>
                          <p className="font-semibold text-sm">{preset.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{preset.description}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><Image className="h-5 w-5 text-primary" /> Logo del Restaurante</CardTitle>
                    <CardDescription>Aparecerá centrado en el header del widget</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className={cn('w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-muted transition-all', restaurantLogo ? 'border-primary bg-primary/5' : 'border-muted-foreground/30')} onClick={() => fileInputRef.current?.click()}>
                        {restaurantLogo ? <img src={restaurantLogo} alt="Logo" className="w-full h-full object-contain rounded-lg" /> : <Upload className="h-8 w-8 text-muted-foreground" />}
                      </div>
                      <div className="flex-1">
                        <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo} className="rounded-xl">{uploadingLogo ? 'Subiendo...' : 'Subir Logo'}</Button>
                        <p className="text-xs text-muted-foreground mt-2">PNG o JPG, máximo 2MB</p>
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><Palette className="h-5 w-5 text-primary" /> Personalización</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div>
                      <Label className="text-sm font-medium">Nombre del Restaurante</Label>
                      <Input value={config.restaurantName} onChange={e => setConfig(p=>({...p,restaurantName:e.target.value}))} placeholder="Mi Restaurante" className="mt-1.5 h-11 rounded-xl" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Color Principal</Label>
                      <div className="flex gap-2 mt-1.5">
                        <div className="flex gap-1">
                          {['#F97316','#EF4444','#8B5CF6','#06B6D4','#10B981','#C9A962'].map(color => (
                            <button key={color} className={cn('w-9 h-9 rounded-xl transition-all', config.primaryColor===color && 'ring-2 ring-offset-2 ring-primary')} style={{ backgroundColor:color }} onClick={() => setConfig(p=>({...p,primaryColor:color}))} />
                          ))}
                        </div>
                        <Input type="color" value={config.primaryColor} onChange={e => setConfig(p=>({...p,primaryColor:e.target.value}))} className="w-11 h-9 p-1 cursor-pointer rounded-xl" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><UtensilsCrossed className="h-5 w-5 text-primary" /> Opciones de Carta</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { key:'showPrice',        label:'Mostrar precios',         desc:'Los clientes ven el precio de cada platillo' },
                      { key:'showAllergens',     label:'Mostrar alérgenos',       desc:'Íconos de alérgenos en los platillos' },
                      { key:'showCategories',    label:'Filtro por categorías',   desc:'Pestañas para filtrar el menú' },
                      { key:'showFeaturedFirst', label:'Destacados primero',      desc:'Los platillos ⭐ aparecen al inicio' },
                    ].map(opt => (
                      <div key={opt.key} className="flex items-start gap-3">
                        <Switch checked={config[opt.key]} onCheckedChange={v => setConfig(p=>({...p,[opt.key]:v}))} className="mt-0.5" />
                        <div><Label className="text-sm font-medium">{opt.label}</Label><p className="text-xs text-muted-foreground">{opt.desc}</p></div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Language selector */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /> Idioma del Widget</CardTitle>
                    <CardDescription>El widget aparecerá en el idioma seleccionado para tus clientes</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {LANGUAGES.map(lang => (
                        <button key={lang.code} onClick={() => setConfig(p=>({...p,language:lang.code}))}
                          className={cn('px-3 py-2.5 rounded-xl border-2 text-sm font-medium text-left transition-all', config.language===lang.code ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted')}>
                          {lang.label}
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Payment options */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> Opciones de Pago</CardTitle>
                    <CardDescription>Elige cómo pueden pagar tus clientes al hacer la reservación</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { id:'restaurant', label:'🏪 Pagar en el restaurante', desc:'El cliente paga al llegar. Sin integración requerida.' },
                      { id:'stripe', label:'💳 Stripe (tarjeta)', desc:'Visa, Mastercard, AMEX. Requiere cuenta Stripe.' },
                      { id:'paypal', label:'🅿️ PayPal', desc:'Pago con cuenta PayPal. Requiere cuenta PayPal Business.' },
                      { id:'okwallet', label:'👛 OkWallet', desc:'El cliente paga con su cartera digital OkWallet. Instantáneo y sin comisión.' },
                    ].map(opt => (
                      <label key={opt.id} className="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all" style={{ borderColor: config.paymentMethods.includes(opt.id) ? 'hsl(var(--primary))' : 'hsl(var(--border))', background: config.paymentMethods.includes(opt.id) ? 'hsl(var(--primary)/.05)' : 'transparent' }}>
                        <input type="checkbox" className="mt-0.5" checked={config.paymentMethods.includes(opt.id)} onChange={e => {
                          const updated = e.target.checked ? [...config.paymentMethods, opt.id] : config.paymentMethods.filter(x=>x!==opt.id);
                          setConfig(p=>({...p,paymentMethods:updated.length?updated:['restaurant']}));
                        }} />
                        <div>
                          <p className="text-sm font-semibold">{opt.label}</p>
                          <p className="text-xs text-muted-foreground">{opt.desc}</p>
                        </div>
                      </label>
                    ))}
                    {(config.paymentMethods.includes('stripe') || config.paymentMethods.includes('paypal')) && (
                      <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">⚡ Para activar pagos en línea, conecta Stripe o PayPal en <strong>Configuración → Integraciones</strong>.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
                  <CardContent className="p-6 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="font-bold flex items-center gap-2"><BookOpen className="h-5 w-5 text-orange-500" /> Gestionar Menús</h3>
                      <p className="text-sm text-muted-foreground mt-1">{totalDishes} platillos en {menus.length} menús activos</p>
                    </div>
                    <Link to="/menus-cartas" className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors whitespace-nowrap text-sm">Editar carta</Link>
                  </CardContent>
                </Card>
              </div>

              {/* Right preview */}
              <div className="space-y-4">
                <Card className="border-0 shadow-lg sticky top-20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2"><Eye className="h-5 w-5 text-primary" /> Vista Previa Interactiva</CardTitle>
                    <CardDescription>4 pasos completos — interactúa como lo harán tus clientes</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingMenus ? (
                      <div className="flex items-center justify-center py-20 gap-3">
                        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                        <span className="text-sm text-muted-foreground">Cargando menús...</span>
                      </div>
                    ) : (
                      <CartaExpressWidget config={config} menus={menus} />
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ── INTEGRATION TAB ── */}
          <TabsContent value="code">
            <WidgetIntegrationPanel
              type="carta"
              restaurantId={restaurantId}
              urlParams={{
                color: config.primaryColor.replace('#', ''),
                theme: config.theme,
                radius: (config.borderRadius || '12px').toString().replace('px', ''),
                lang: config.language,
              }}
              sdkConfig={config}
              borderRadius={config.borderRadius}
              productionBase={PRODUCTION_URL}
              embedTitle="OkReserva Carta Express Widget"
            />
          </TabsContent>

          {/* ── SHARE TAB ── */}
          <TabsContent value="share">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Compartir</CardTitle><CardDescription>Comparte tu widget de carta express</CardDescription></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-muted rounded-xl">
                      <Label className="text-sm font-medium">Link directo</Label>
                      <div className="flex gap-2 mt-2">
                        <Input value={`${PRODUCTION_URL}/widget/carta/${restaurantId}`} readOnly className="rounded-xl" />
                        <Button variant="outline" className="rounded-xl" onClick={() => { navigator.clipboard.writeText(`${PRODUCTION_URL}/widget/carta/${restaurantId}`); toast.success('Link copiado'); }}><Copy className="h-4 w-4" /></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Button variant="outline" className="h-14 rounded-xl flex items-center gap-3" onClick={()=>window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(PRODUCTION_URL+'/widget/carta/'+restaurantId)}`,'_blank')}><Facebook className="h-5 w-5 text-blue-600" /> Facebook</Button>
                      <Button variant="outline" className="h-14 rounded-xl flex items-center gap-3" onClick={()=>window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(PRODUCTION_URL+'/widget/carta/'+restaurantId)}`,'_blank')}><Twitter className="h-5 w-5 text-sky-500" /> Twitter/X</Button>
                      <Button variant="outline" className="h-14 rounded-xl flex items-center gap-3 col-span-2" onClick={()=>toast.info('Copia el link y pégalo en tu bio')}><Instagram className="h-5 w-5 text-pink-500" /> Instagram (bio)</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <Card className="border-0 shadow-lg">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" /> Código QR</CardTitle><CardDescription>Imprime y coloca en tu local</CardDescription></CardHeader>
                <CardContent className="text-center">
                  <div className="w-48 h-48 mx-auto bg-white rounded-xl p-2 mb-4 border">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(PRODUCTION_URL + '/widget/carta/' + restaurantId)}`}
                      alt="QR Carta Express"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex gap-2 justify-center">
                    <a href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(PRODUCTION_URL + '/widget/carta/' + restaurantId)}`} download="carta-qr.png" target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="rounded-xl">Descargar QR</Button>
                    </a>
                    <Button className="rounded-xl" onClick={() => { navigator.clipboard.writeText(PRODUCTION_URL + '/widget/carta/' + restaurantId); toast.success('URL copiada'); }}>
                      <Copy className="h-4 w-4 mr-1" /> Copiar URL
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── CARTA TAB ── */}
          <TabsContent value="carta">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardHeader><CardTitle className="text-lg flex items-center gap-2"><UtensilsCrossed className="h-5 w-5 text-primary" /> Menús Activos</CardTitle><CardDescription>Solo los menús activos aparecen en el widget</CardDescription></CardHeader>
                  <CardContent>
                    {loadingMenus ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
                    : menus.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-3">🍽️</div>
                        <p className="text-muted-foreground text-sm mb-3">No hay menús activos</p>
                        <Link to="/menus-cartas"><Button size="sm"><Plus className="h-4 w-4 mr-1.5" /> Crear menú</Button></Link>
                      </div>
                    ) : menus.map(menu => (
                      <div key={menu.id} className="flex items-start gap-3 p-3 rounded-xl border mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{menu.name}</p>
                          <p className="text-xs text-muted-foreground">{menu.dishes?.length||0} platillos</p>
                          <div className="flex flex-wrap gap-1 mt-1">{[...new Set((menu.dishes||[]).map(d=>d.category))].slice(0,4).map(c=><Badge key={c} variant="outline" className="text-xs px-1.5 py-0 h-4">{c}</Badge>)}</div>
                        </div>
                        <Badge className="bg-green-100 text-green-700">Activo</Badge>
                      </div>
                    ))}
                    {menus.length>0 && <Link to="/menus-cartas"><Button variant="outline" className="w-full mt-2 rounded-xl"><Settings className="h-4 w-4 mr-2" /> Gestionar Menús</Button></Link>}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Zap className="h-5 w-5 text-primary" /> Cómo funciona</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { e:'📋', t:'Paso 1 — Información', d:'El cliente indica a nombre de quién es, la ocasión, alergias, peticiones y requerimientos especiales.' },
                      { e:'📅', t:'Paso 2 — Fecha y hora', d:'Selecciona cuántas personas son, qué día y a qué hora quiere venir.' },
                      { e:'🍽️', t:'Paso 3 — Elige tu carta', d:'Navega los menús activos y agrega platillos al pedido anticipado con detalle y cantidad.' },
                      { e:'✅', t:'Paso 4 — Confirma', d:'Resumen completo y confirmación de la reservación con el pre-pedido incluido.' },
                    ].map(s=><div key={s.t} className="flex items-start gap-3 p-3 bg-muted rounded-xl"><span className="text-xl flex-shrink-0">{s.e}</span><div><p className="font-medium text-sm">{s.t}</p><p className="text-xs text-muted-foreground">{s.d}</p></div></div>)}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardHeader><CardTitle className="text-lg">Beneficios del pedido anticipado</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { e:'⚡', t:'Servicio más rápido', d:'La cocina se prepara antes de que lleguen.' },
                      { e:'💰', t:'Mayor ticket promedio', d:'Los clientes eligen más platillos sin presión.' },
                      { e:'😊', t:'Mejor experiencia', d:'Sin esperas — el cliente llega y disfruta.' },
                      { e:'📊', t:'Datos de demanda', d:'Sabes qué platillos se piden antes de abrir.' },
                    ].map(b=><div key={b.t} className="flex items-start gap-3 p-3 border rounded-xl"><span className="text-xl flex-shrink-0">{b.e}</span><div><p className="font-medium text-sm">{b.t}</p><p className="text-sm text-muted-foreground">{b.d}</p></div></div>)}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-2xl bg-orange-500 flex items-center justify-center"><ShoppingCart className="h-7 w-7 text-white" /></div>
                      <div><h3 className="font-bold text-lg">Carta Express activada</h3><p className="text-sm text-muted-foreground">{menus.length} menú{menus.length!==1?'s':''} · {totalDishes} platillo{totalDishes!==1?'s':''}</p></div>
                    </div>
                    <Badge className="bg-green-500/10 text-green-600">✓ Activo</Badge>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
